/** User-facing copy for Akahu pay-by-bank. Lead with the action, disclose Akahu second. */

export const AKAHU_PRODUCT_NAME = "Pay by bank";

export const AKAHU_PROVIDER_NAME = "Akahu";

export const AKAHU_ABOUT =
  "Akahu is New Zealand's open finance platform. MixItOne uses Akahu for one-off pay-by-bank payments and one-time account checks. MixItOne does not keep ongoing access to your bank.";

export const AKAHU_LEARN_MORE_URL = "https://www.akahu.nz";

export const AKAHU_METHOD_NAME = "Pay by bank";

export const AKAHU_METHOD_DESCRIPTION = "Pay from your NZ bank account.";

export const AKAHU_METHOD_INSTRUCTIONS = (amount?: string) =>
  amount
    ? `Continue to your bank to authorise a one-off $${amount} NZD payment, then return here to finish registration.`
    : "Continue to your bank to authorise a one-off payment, then return here to finish registration.";

export const AKAHU_METHOD_ONEOFF_NOTE = (amount?: string) =>
  amount
    ? `Next will send you to your bank to authorise this one-off $${amount} NZD payment. You will return here afterwards. MixItOne does not keep access to your bank.`
    : "Next will send you to your bank to authorise this one-off payment. You will return here afterwards. MixItOne does not keep access to your bank.";

export const AKAHU_AMOUNT_HELP = "Tournament registration fee charged via pay by bank";

/** Admin setting name for the configurable one-off fee cap (NZD). */
export const AKAHU_ONE_OFF_MAX_AMOUNT_SETTING = "AKAHU_ONE_OFF_MAX_AMOUNT";

/** Fallback when the admin setting is missing or unreadable (Akahu sandbox limit). */
export const AKAHU_ONE_OFF_MAX_AMOUNT_DEFAULT = 5;

export const AKAHU_AMOUNT_LIMIT_HELP = (maxAmount: number) =>
  `Tournament registration fee charged via pay by bank. Maximum $${formatAkahuAmount(maxAmount)} NZD.`;

export const AKAHU_AMOUNT_OVER_LIMIT = (maxAmount: number) =>
  `Pay by bank amount cannot exceed $${formatAkahuAmount(maxAmount)} NZD`;

export function formatAkahuAmount(amount: number): string {
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}

/** Player-facing amount with cents, for checkout confirmation. */
export function formatAkahuPlayerAmount(amount: unknown): string | undefined {
  const value = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(value)) return undefined;
  return value.toFixed(2);
}

export const AKAHU_CLUB_CONNECT_TITLE = "Connect your club bank account";

export const AKAHU_CLUB_CONNECT_BODY =
  "Connect the club bank account that should receive pay-by-bank payments. Players pay from their own NZ bank.";

export const AKAHU_CLUB_CONNECT_REDIRECT = "You will be redirected to connect your bank, then returned here.";

export const AKAHU_CLUB_CONNECT_READY =
  "Ready to receive pay-by-bank payments. Keep another payment method available for players whose bank is not supported.";

export const AKAHU_CLUB_CONNECT_CTA = "Connect club bank account";

export const AKAHU_CLUB_CONNECT_EDIT_CTA = "Reconnect club bank account";

export const AKAHU_CLUB_CONNECT_SUCCESS = "Club bank account connected and ready for pay-by-bank fees.";

/** One-off identity snapshot of name + account number. No enduring tokens. */
export const AKAHU_CLUB_ONEOFF_TITLE = "Confirm your club bank account";

export const AKAHU_CLUB_ONEOFF_BODY =
  "Confirm the club bank account that should receive pay-by-bank payments. This is a one-time check. Players pay from their own NZ bank.";

export const AKAHU_CLUB_ONEOFF_REDIRECT =
  "You will leave briefly for a one-time bank check, then return here. MixItOne does not keep ongoing access to the club bank.";

export const AKAHU_CLUB_ONEOFF_SUCCESS =
  "Club bank account confirmed. Players can make one-off pay-by-bank payments to this account.";

export const AKAHU_CLUB_ONEOFF_READY =
  "This account is confirmed to receive one-off pay-by-bank payments. Keep another payment method available for players whose bank is not supported.";

export const AKAHU_CLUB_ONEOFF_CTA = "Confirm club bank account";

export const AKAHU_CLUB_ONEOFF_EDIT_CTA = "Confirm a different club bank account";

/** Club-facing copy for enduring OAuth vs the one-off identity check. */
export function getAkahuClubSetupCopy(hasEnduring: boolean) {
  if (hasEnduring) {
    return {
      title: AKAHU_CLUB_CONNECT_TITLE,
      body: AKAHU_CLUB_CONNECT_BODY,
      redirect: AKAHU_CLUB_CONNECT_REDIRECT,
      ready: AKAHU_CLUB_CONNECT_READY,
      success: AKAHU_CLUB_CONNECT_SUCCESS,
      cta: AKAHU_CLUB_CONNECT_CTA,
      editCta: AKAHU_CLUB_CONNECT_EDIT_CTA,
      statusReady: "Connected",
      statusNotReady: "Not connected",
      headingReady: "Club bank account connected",
      confirmedOnPrefix: "Connected",
      mfaReason: "Confirm with your authenticator app to connect the club bank account.",
      notReadyToSave: "Connect the club bank account before saving this pay by bank payment method.",
      startError: "Failed to start bank account connection",
      failed: "Bank account connection failed",
      loadError: "Failed to load bank connection",
      checking: "Checking bank connection…",
      previousFailed: "Previous connection failed.",
    } as const;
  }

  return {
    title: AKAHU_CLUB_ONEOFF_TITLE,
    body: AKAHU_CLUB_ONEOFF_BODY,
    redirect: AKAHU_CLUB_ONEOFF_REDIRECT,
    ready: AKAHU_CLUB_ONEOFF_READY,
    success: AKAHU_CLUB_ONEOFF_SUCCESS,
    cta: AKAHU_CLUB_ONEOFF_CTA,
    editCta: AKAHU_CLUB_ONEOFF_EDIT_CTA,
    statusReady: "Confirmed",
    statusNotReady: "Not confirmed",
    headingReady: "Club bank account confirmed",
    confirmedOnPrefix: "Confirmed",
    mfaReason: "Confirm with your authenticator app to check the club bank account.",
    notReadyToSave: "Confirm the club bank account before saving this pay by bank payment method.",
    startError: "Failed to start the bank account check",
    failed: "Bank account confirmation failed",
    loadError: "Failed to load bank account details",
    checking: "Checking club bank account…",
    previousFailed: "Previous bank check failed.",
  } as const;
}

/** @deprecated Manual entry removed; kept for any leftover imports. */
export const AKAHU_CLUB_MANUAL_TITLE = AKAHU_CLUB_ONEOFF_TITLE;

/** @deprecated Manual entry removed; kept for any leftover imports. */
export const AKAHU_CLUB_MANUAL_BODY = AKAHU_CLUB_ONEOFF_BODY;

/** @deprecated Manual entry removed; kept for any leftover imports. */
export const AKAHU_CLUB_MANUAL_CTA = AKAHU_CLUB_ONEOFF_CTA;

/** @deprecated Manual entry removed; kept for any leftover imports. */
export const AKAHU_CLUB_MANUAL_EDIT_CTA = AKAHU_CLUB_ONEOFF_EDIT_CTA;

export const AKAHU_CLUB_VERIFY_TITLE = "Verify this account";

export const AKAHU_CLUB_VERIFY_BODY =
  "Send a $1 verification payment from your bank to this club account, then check the club statement. We recommend verifying the details before you collect tournament fees.";

export const AKAHU_CLUB_VERIFY_CTA = "Send $1 verification";

export const AKAHU_CLUB_VERIFY_CONFIRM_CTA = "Confirm $1 received";

export const AKAHU_CLUB_VERIFY_NOT_RECEIVED_CTA = "Payment not on statement";

export const AKAHU_CLUB_VERIFY_NUDGE =
  "Verify the club account with a $1 payment before collecting tournament fees.";

export const AKAHU_CLUB_VERIFY_IN_PROGRESS = "Waiting for your bank to finish the verification payment…";

export const AKAHU_CLUB_VERIFY_STILL_PENDING =
  "The bank has not finished yet. You can leave this page and check again later.";

export const AKAHU_CLUB_VERIFY_FAILED =
  "That verification payment did not complete. Check the account number and try again.";

export const AKAHU_CLUB_VERIFY_CANCELLED_FLASH =
  "Verification payment cancelled. Send a $1 verification when you are ready to continue.";

export const AKAHU_CLUB_VERIFY_DETAILS_CHANGED =
  "Bank details changed, so this account needs to be verified again. Send a $1 verification payment to the new account, then confirm it on the club statement.";

/** Stored on cancelled verification attempts when manual payee details change. */
export const AKAHU_CLUB_VERIFY_DETAILS_CHANGED_REASON = "Payee details updated";

export const AKAHU_CLUB_VERIFY_READY_TO_CONFIRM_BEFORE =
  "Confirm only after the $1 shows on the club statement. Allow up to ";

export const AKAHU_CLUB_VERIFY_READY_WAIT = "one business day";

export const AKAHU_CLUB_VERIFY_READY_TO_CONFIRM_AFTER =
  " for it to appear. If it is still missing after that, choose Payment not on statement and check the account number.";

export const AKAHU_CLUB_VERIFIED_LABEL = "Account verified";

export const AKAHU_CLUB_VERIFIED_ON = (when: string) => `Verified: ${when}`;

export const AKAHU_CLUB_UNVERIFIED_LABEL = "Not verified";

export const AKAHU_CLUB_LIMITED_BANKS = "Pay by bank only works with a subset of NZ banks.";

export const AKAHU_PLAYER_LIMITED_BANKS =
  "Only the banks listed below can be used for pay by bank. If yours is not listed, choose another payment method.";

export const AKAHU_NAV_LABEL = "Pay by bank";

export const AKAHU_SETTINGS_TITLE = "Pay by bank setup";

export const AKAHU_ABOUT_PANEL_TITLE = "About pay by bank";
